import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalNoticeUI } from '@cube3/ui/src/Modal/ModalNoticeUI';
import { useModalActions } from '../../../../../../app/layout/Modals/modalActions';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import {
  selectors,
  statuses
} from '@cube3/state/src/redux/ducks/request-status';
import { actions } from '@cube3/state/src/redux/ducks/resource-nodes';
import { useMutateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useMutateResource';
import { Team } from '@cube3/common/model/schema/resources/team';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';
import { useGenericFormState } from '@cube3/forms/src/hooks/useGenericFormState';
import { GenericForm } from '@cube3/forms/src/forms/genericForm/GenericForm';
import { objectFromValues } from '@cube3/forms/src/utils/objectToFormValues';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import { getErrorMessage } from '@cube3/forms/src/utils/getErrorMessage';

interface Props {
  modalContext: {
    team: Team;
    workspaceId: string;
  };
}

const fields: Field[] = [
  {
    id: 'team_name',
    type: 'text',
    label: 'Team name',
    placeholder: 'Enter a team name',
    required: true,
    requiredLabel: '',
    fullWidth: true
  }
];

const TeamSettingsModal = (props: Props) => {
  const { team, workspaceId } = props.modalContext;

  const { previousModal, openModal } = useModalActions();
  const [errorState, setError] = useState(undefined);

  const initialValues = useMemo(() => {
    return [
      {
        id: 'team_name',
        value: team?.display_name
      }
    ];
  }, [team]);

  const {
    fields: fieldsWithState,
    values,
    dirty,
    valid
  } = useGenericFormState({ fields, initialValues });

  const [mutateTeam, mutateTeamStatus] = useMutateResource__ALPHA({
    cacheInvalidator: useCallback(
      () => [
        {
          type: 'workspace',
          id: workspaceId,
          relationships: 'teams'
        }
      ],
      [workspaceId]
    )
  });

  const onClickSave = useCallback(() => {
    const value = objectFromValues(values);
    const newTeamName = (value.team_name as string)?.trim();
    mutateTeam({
      type: 'team',
      id: team.id,
      display_name: newTeamName
    });
  }, [mutateTeam, team.id, values]);

  useEffect(() => {
    if (mutateTeamStatus === statuses.SUCCESS) {
      previousModal();
    }
  }, [mutateTeamStatus, previousModal]);

  const onClickDelete = useCallback(() => {
    openModal('workspace_teams_delete', { team }, false);
  }, [openModal, team]);

  const errors = useTypedSelector((state) =>
    selectors.getErrors(state.requestStatus, {
      type: actions.MUTATE_RESOURCE,
      meta: { apiClient: { type: 'team', id: team?.id } }
    })
  );

  const submitError = errorState && getErrorMessage(errorState);
  const errorMsg = submitError;

  useEffect(() => {
    if (mutateTeamStatus === statuses.FAILED) {
      setError(errors && getErrorMessage(errors));
    }
  }, [errors, mutateTeamStatus]);

  const loading =
    mutateTeamStatus &&
    mutateTeamStatus !== statuses.SUCCESS &&
    mutateTeamStatus !== statuses.FAILED;

  return (
    <ModalNoticeUI
      title="Team Settings"
      onCloseEvent={previousModal}
      loading={loading}
      footerRightComponent={
        !dirty ? (
          <Button
            label="Done"
            buttonStyle="solid"
            background="primary"
            onClick={previousModal}
          />
        ) : (
          <>
            <Button
              label="Cancel"
              buttonStyle="ghost"
              onClick={previousModal}
            />
            <Button
              label="Save changes"
              buttonStyle="solid"
              background="primary"
              onClick={onClickSave}
              disabled={!!errorMsg || !valid}
            />
          </>
        )
      }
      footerLeftComponent={
        <Button
          iconLeft={'delete'}
          label="Delete team"
          background="critical"
          onClick={onClickDelete}
        />
      }
    >
      <div style={{ minHeight: 200 }}>
        <GenericForm fields={fieldsWithState} direction="vertical" />
        {!!errorMsg && <Typography color="critical">{errorMsg}</Typography>}
      </div>
    </ModalNoticeUI>
  );
};

export default TeamSettingsModal;
